import {
  type Log,
  LogLevels,
  type LoggerConfiguration,
  type LogLevel as OurLogLevel,
} from '@backmarket/front-logger'
import { LogLevel, faro } from '@grafana/faro-web-sdk'

import { flattenAttributes } from './flattenAttributes'

const faroLogLevels: Record<OurLogLevel, LogLevel> = {
  [LogLevels.DEBUG]: LogLevel.DEBUG,
  [LogLevels.INFO]: LogLevel.INFO,
  [LogLevels.ERROR]: LogLevel.ERROR,
}

export function createFaroTransport({
  level,
}: { level?: OurLogLevel } = {}): LoggerConfiguration['transports'][number] {
  return {
    level,
    name: 2,
    log(transformedLog: Log) {
      // The Faro singleton is initialised by nuxt-module-instrumentation's
      // client plugin, which runs after the logger plugin. Guard against the
      // window check (SSR safety) and against the logger emitting before Faro
      // has finished initialising.
      if (typeof window === 'undefined') return
      if (!faro?.api) return

      const logLevel = LogLevels[transformedLog.SeverityText]
      const context = flattenAttributes(transformedLog.Attributes)

      if (logLevel === LogLevels.ERROR) {
        // pushError sends a dedicated error event which Grafana surfaces
        // separately from the log stream. We still call pushLog below so the
        // error also appears in the log stream with its structured context.
        //
        // When the caller attached an Error instance, rebuild it so Faro gets
        // a real stack trace. When they didn't (e.g. `logger.error('msg')`),
        // synthesize one from the message body so the event still reaches
        // the error stream — without this fallback, error-level logs without
        // an Error attached are silently missing from Grafana's errors view.
        const error = transformedLog.error
          ? Object.assign(new Error(transformedLog.error.message), {
              name: transformedLog.error.name,
              stack: transformedLog.error.stack,
              cause: transformedLog.error.cause,
            })
          : new Error(transformedLog.Body)
        faro.api.pushError(error, { context })
      }

      faro.api.pushLog([transformedLog.Body], {
        level: faroLogLevels[logLevel],
        context,
      })
    },
  }
}
